export const THAI_MONTH_NAMES = [
  "มกราคม",
  "กุมภาพันธ์",
  "มีนาคม",
  "เมษายน",
  "พฤษภาคม",
  "มิถุนายน",
  "กรกฎาคม",
  "สิงหาคม",
  "กันยายน",
  "ตุลาคม",
  "พฤศจิกายน",
  "ธันวาคม",
];
export const ENGLISH_MONTH_NAMES = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
];
export const DAYS_OF_WEEK = [
  "อาทิตย์",
  "จันทร์",
  "อังคาร",
  "พุธ",
  "พฤหัสบดี",
  "ศุกร์",
  "เสาร์",
];
const ISO_PATTERN = /^\d{4}-\d{2}-\d{2}$/;
const DAY_MONTH_YEAR_PATTERN = /^\d{1,2}\/\d{1,2}\/\d{4}$/;
const BUDDHIST_ERA_THRESHOLD = 2400;
const BUDDHIST_YEAR_OFFSET = 543;

/** * Get the week (start and end dates) for a given date.
 *
 * @param {Date} date The date to get the week for.
 * @returns {Object} An object containing the startDate and endDate of the week.
 */
export function weekFromDate(date) {
  const dayOfWeek = date.getDay(); // 0 (Sun) - 6 (Sat)
  const startOfWeek = new Date(date);
  startOfWeek.setDate(date.getDate() - dayOfWeek);

  const endOfWeek = new Date(startOfWeek);
  endOfWeek.setDate(startOfWeek.getDate() + 6);

  return { startDate: startOfWeek, endDate: endOfWeek };
}

/**
 * Format a date into a Thai locale string.
 *
 * @param {Date|string} date The date to format.
 * @param {Object} options Formatting options.
 * @param {string} [options.month="long"] The month format ("long", "short", "numeric").
 * @param {boolean} [options.includeYear=true] Whether to include the year in the output.
 * @param {boolean} [options.includeDayOfWeek=false] Whether to include the day of the week in the output.
 */
export function formatThaiDate(
  date,
  {
    month = "long",
    includeYear = true,
    includeDayOfWeek = false,
    useChristianYear = false,
    useEnglish = false,
  } = {},
) {
  if (!date) return "";
  const safeDate = date instanceof Date ? date : new Date(date);
  if (Number.isNaN(safeDate?.getTime?.())) return "";

  const opts = { day: "numeric", month };
  if (includeYear) opts.year = "numeric";
  if (includeDayOfWeek) opts.weekday = "long";

  if (useEnglish) {
    const day = safeDate.getDate();
    const monthName = ENGLISH_MONTH_NAMES[safeDate.getMonth()];
    const year = safeDate.getFullYear();
    let result = `${day} ${monthName}`;
    if (includeYear) result += ` ${year}`;
    if (includeDayOfWeek) {
      const dayName = [
        "Sunday",
        "Monday",
        "Tuesday",
        "Wednesday",
        "Thursday",
        "Friday",
        "Saturday",
      ][safeDate.getDay()];
      result = `${dayName}, ${result}`;
    }
    return result;
  }

  const locale = useChristianYear ? "th-TH-u-ca-gregory" : "th-TH";
  return safeDate.toLocaleDateString(locale, opts).replace("วัน", "").trim();
}

/**
 * Format a date range representing the week of the given date.
 * For example, "11 ม.ค. - 17 ม.ค. 2569".
 *
 * @param {Date} date The date to format.
 * @returns {string} A string representing the week range of the given date.
 */
export function formatWeekRangeFromDate(date, { month = "short" } = {}) {
  const { startDate: startOfWeek, endDate: endOfWeek } = weekFromDate(date);

  const startStr = formatThaiDate(startOfWeek, {
    month: month,
    // Omit start year if same as end year
    includeYear: startOfWeek.getFullYear() !== endOfWeek.getFullYear(),
  });
  const endStr = formatThaiDate(endOfWeek, {
    month: month,
    includeYear: true,
  });

  return `${startStr} - ${endStr}`;
}

/**
 * Format time from a given date to "HH:MM" format.
 *
 * @param {Date|string} date The date to extract time from.
 */
export function formatTimeFromDate(date) {
  const safeDate = date instanceof Date ? date : new Date(date);
  if (Number.isNaN(safeDate?.getTime?.())) return "";

  const hours = String(safeDate.getHours()).padStart(2, "0");
  const minutes = String(safeDate.getMinutes()).padStart(2, "0");
  return `${hours}:${minutes}`;
}

/**
 * Format a Date object into "YYYY-MM-DD" string.
 *
 * @param {Date} date The date to format.
 */
export function formatDate(date) {
  if (!(date instanceof Date)) return;
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
}

/**
 * Format a Date object into "DD/MM/YYYY" string for certificate APIs.
 *
 * @param {Date|string} date The date to format.
 */
export function formatDayMonthYear(date) {
  if (!date) return "";
  const safeDate = date instanceof Date ? date : new Date(date);
  if (Number.isNaN(safeDate?.getTime?.())) return "";

  const day = String(safeDate.getDate()).padStart(2, "0");
  const month = String(safeDate.getMonth() + 1).padStart(2, "0");
  const year = safeDate.getFullYear();

  return `${day}/${month}/${year}`;
}

/**
 * Convert a Thai display string (e.g. "9 สิงหาคม 2540") into "YYYY-MM-DD".
 * Returns an empty string if the input cannot be parsed.
 */
export function convertThaiDisplayToIso(dateString) {
  if (!dateString || typeof dateString !== "string") return "";
  const trimmed = dateString.trim();
  if (ISO_PATTERN.test(trimmed)) {
    return trimmed;
  }
  if (DAY_MONTH_YEAR_PATTERN.test(trimmed)) {
    const [day, month, year] = trimmed.split("/");
    return `${year}-${month.padStart(2, "0")}-${day.padStart(2, "0")}`;
  }

  const match = trimmed.match(/^(\d{1,2})\s+(.+?)\s+(\d{4})$/);
  if (!match) {
    return "";
  }

  const day = parseInt(match[1], 10);
  let monthIdx = THAI_MONTH_NAMES.indexOf(match[2]);
  if (monthIdx === -1) {
    monthIdx = ENGLISH_MONTH_NAMES.indexOf(match[2]);
  }
  if (monthIdx === -1 || Number.isNaN(day)) {
    return "";
  }

  let year = parseInt(match[3], 10);
  if (Number.isNaN(year)) {
    return "";
  }

  if (year >= BUDDHIST_ERA_THRESHOLD) {
    year -= BUDDHIST_YEAR_OFFSET;
  }

  const month = String(monthIdx + 1).padStart(2, "0");
  const formattedDay = String(day).padStart(2, "0");

  return `${year}-${month}-${formattedDay}`;
}

/**
 * Prefix a Thai date display string with "วันที่".
 * Returns the input untouched when empty or the API's "-" placeholder.
 *
 * @param {string} date The date display string to prefix.
 */
export function addDatePrefix(date) {
  if (!date || date === "-") return date ?? "";
  return `วันที่ ${date}`;
}
